import {
    Box,
    Paragraph,
    Anchor,
    Avatar,
    IconCheckmarkCircleOutline,
    Image,
    MediaObject,
    Popover,
    Popper,
} from '@zillow/constellation';
import { debounce } from 'lodash';
import React, { useState, useEffect } from 'react';

import type { Contributor } from '../../B2CPostTemplate.types';
import ContributorPopover from '../ContributorPopover/ContributorPopover';

export interface ContributorsCardProps {
    author: Contributor;
    reviewers?: Contributor[];
    editors?: Contributor[];
}

const ContributorsCard: React.FC<ContributorsCardProps> = ({
    author,
    reviewers = [],
    editors = [],
}) => {
    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        const onResize = () => {
            setIsMobile(window.innerWidth < 890);
        };
        const debouncedOnResize = debounce(onResize, 100);

        setIsMobile(window.innerWidth < 890);
        // eslint-disable-next-line no-undef
        window.addEventListener('resize', debouncedOnResize);
        window.addEventListener('orientationchange', debouncedOnResize);

        return () => {
            // eslint-disable-next-line no-undef
            window.removeEventListener('resize', debouncedOnResize);
            window.removeEventListener('orientationchange', debouncedOnResize);
        };
    }, []);

    // Helper to render name with popover
    const renderName = (contributor: Contributor, id: string) => {
        if (isMobile) {
            if (!contributor.link) {
                return contributor.name;
            }
            return (
                <Anchor id={id} href={contributor.link} target="_blank">
                    {contributor.name}
                </Anchor>
            );
        }
        return (
            <Popper triggered={popover(contributor)}>
                <Anchor id={id} onClick={(e) => e.preventDefault()}>
                    {contributor.name}
                </Anchor>
            </Popper>
        );
    };

    const popover = (contributor: Contributor) => (
        <Popover body={<ContributorPopover contributor={contributor} />} />
    );

    return (
        <>
            {/* Author with MediaObject */}
            <MediaObject
                align="center"
                gutter="sm"
                media={
                    author.avatar ? (
                        <Box
                            as={Avatar}
                            size="md"
                            sx={{
                                display: 'block',
                            }}
                        >
                            <Image src={author.avatar} alt={author.name} />
                        </Box>
                    ) : null
                }
            >
                <>
                    <Paragraph fontType="bodySmall" fontColor="textMedium">
                        Written by {renderName(author, `author-${author.name}`)}
                        {author.date && <> on {author.date}</>}
                    </Paragraph>
                    {(!!reviewers.length || !!editors.length) && (
                        <MediaObject
                            align="top"
                            gutter="NONE"
                            media={
                                <Box
                                    as={IconCheckmarkCircleOutline}
                                    fontColor="borderGreen"
                                    size="xs"
                                    sx={{
                                        display: 'block',
                                        marginRight: '$spacing.xxs',
                                        marginTop: '$spacing.xxs',
                                    }}
                                />
                            }
                        >
                            <Paragraph
                                fontType="bodySmall"
                                fontColor="textMedium"
                            >
                                {!!reviewers.length && (
                                    <>
                                        Reviewed by{' '}
                                        {reviewers.map((r, i) => (
                                            <React.Fragment key={r.name}>
                                                {i > 0 && ' and '}
                                                {renderName(
                                                    r,
                                                    `reviewer-${r.name}`
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                                {!!reviewers.length && !!editors.length && ', '}
                                {!!editors.length && (
                                    <>
                                        Edited by{' '}
                                        {editors.map((e, i) => (
                                            <React.Fragment key={e.name}>
                                                {i > 0 && ' and '}
                                                {renderName(
                                                    e,
                                                    `editor-${e.name}`
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                            </Paragraph>
                        </MediaObject>
                    )}
                </>
            </MediaObject>
        </>
    );
};

export default ContributorsCard;
